/**
 * Admin V3 — Corporate employees management modal (AV3 A-6)
 *
 * Per-corporate-account employee management, reusing the canonical endpoints:
 *   - GET    /corporate/{id}/employees                          (employee:list)
 *   - POST   /corporate/{id}/employees                          (employee:create — sends invitation)
 *   - DELETE /employees/{employeeId}                            (employee:delete)
 *   - POST   /corporate/{id}/employees/{employeeId}/resend-invitation (employee:resend_invite)
 *
 * Backend owns invitation emails and uniqueness. No new business logic, no
 * backend change. Each mutation is RBAC-gated (HIDE posture; backend authoritative).
 */
import React, { useState } from 'react';
import { Loader2, X, UserPlus, Trash2, Send, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-[13px] text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

const PERM_LIST = 'employee:list';
const PERM_CREATE = 'employee:create';
const PERM_DELETE = 'employee:delete';
const PERM_RESEND = 'employee:resend_invite';

// Module-scope per-employee row: each row owns its delete + resend mutation hooks.
const EmployeeRow = ({ employee, accountId, canDelete, canResend, onDone, locale }) => {
  const del = useAdminMutation(`/employees/${employee.id}`, 'delete');
  const resend = useAdminMutation(`/corporate/${accountId}/employees/${employee.id}/resend-invitation`, 'post');
  const { showSuccess, showError } = useToast();

  const doDelete = () => del.mutate({}, {
    onSuccess: () => { showSuccess(t('corporate.employees.removed', locale)); if (onDone) onDone(); },
    onError: (e) => showError(e?.response?.data?.detail || t('corporate.employees.removeError', locale)),
  });
  const doResend = () => resend.mutate({}, {
    onSuccess: () => showSuccess(t('corporate.employees.resent', locale)),
    onError: (e) => showError(e?.response?.data?.detail || t('corporate.employees.resendError', locale)),
  });

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 text-[13px]" data-testid="adminv3-corporate-employee-row">
      <div className="min-w-0">
        <div className="truncate font-medium text-slate-800">{employee.first_name} {employee.last_name}</div>
        <div className="truncate text-[11px] text-slate-400">{employee.email} · {t(`corporate.employeeStatus.${employee.status}`, locale) || employee.status}</div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        {canResend && employee.status === 'pending' && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={doResend} disabled={resend.loading} data-testid="adminv3-corporate-employee-resend">
            {resend.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        )}
        {canDelete && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px] text-rose-600 hover:bg-rose-50" onClick={doDelete} disabled={del.loading} data-testid="adminv3-corporate-employee-remove">
            {del.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>
    </div>
  );
};

const CorporateEmployeesModal = ({ open, enterprise, onClose, locale = DEFAULT_LOCALE }) => {
  const accountId = enterprise?.id;
  const { can } = usePermissions();
  const { showSuccess, showError } = useToast();

  const { data, loading, refetch } = useAdminQuery(
    `/corporate/${accountId || '_'}/employees`,
    {},
    { enabled: Boolean(open && accountId && can(PERM_LIST)) },
  );
  const addM = useAdminMutation(`/corporate/${accountId || '_'}/employees`, 'post');

  const [add, setAdd] = useState({ email: '', first_name: '', last_name: '', department: '', monthly_budget: '' });
  const setA = (k) => (e) => setAdd((p) => ({ ...p, [k]: e.target.value }));

  if (!open || !enterprise) return null;

  const employees = Array.isArray(data) ? data : [];
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(add.email.trim());
  const addValid = emailOk && add.first_name.trim() && add.last_name.trim();

  const submitAdd = () => {
    addM.mutate(
      {
        email: add.email.trim(),
        first_name: add.first_name.trim(),
        last_name: add.last_name.trim(),
        department: add.department.trim() || null,
        monthly_budget: add.monthly_budget === '' ? null : Number(add.monthly_budget),
      },
      {
        onSuccess: () => {
          showSuccess(t('corporate.employees.added', locale));
          setAdd({ email: '', first_name: '', last_name: '', department: '', monthly_budget: '' });
          refetch();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('corporate.employees.addError', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-corporate-employees-modal">
      <div className="w-[600px] max-w-[94vw] max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
            <Users className="h-4 w-4 text-indigo-500" />
            {t('corporate.employees.title', locale).replace('{name}', enterprise.name || '')}
          </h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-corporate-employees-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Existing employees */}
        <div className="space-y-2">
          {loading && !data && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />{t('corporate.employees.loading', locale)}
            </div>
          )}
          {!loading && employees.length === 0 && (
            <p className="py-2 text-[13px] text-slate-400" data-testid="adminv3-corporate-employees-empty">{t('corporate.employees.empty', locale)}</p>
          )}
          {employees.map((emp) => (
            <EmployeeRow
              key={emp.id}
              employee={emp}
              accountId={accountId}
              canDelete={can(PERM_DELETE)}
              canResend={can(PERM_RESEND)}
              onDone={refetch}
              locale={locale}
            />
          ))}
        </div>

        {/* Add employee */}
        {can(PERM_CREATE) && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="adminv3-corporate-employees-add">
            <p className="mb-2 text-[12px] font-semibold text-slate-600">{t('corporate.employees.addTitle', locale)}</p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <input placeholder={t('corporate.field.email', locale)} value={add.email} onChange={setA('email')} className={inputCls} type="email" data-testid="adminv3-corporate-employee-add-email" />
              <input placeholder={t('corporate.field.department', locale)} value={add.department} onChange={setA('department')} className={inputCls} data-testid="adminv3-corporate-employee-add-department" />
              <input placeholder={t('corporate.field.firstName', locale)} value={add.first_name} onChange={setA('first_name')} className={inputCls} data-testid="adminv3-corporate-employee-add-first" />
              <input placeholder={t('corporate.field.lastName', locale)} value={add.last_name} onChange={setA('last_name')} className={inputCls} data-testid="adminv3-corporate-employee-add-last" />
              <input placeholder={t('corporate.field.monthlyBudget', locale)} value={add.monthly_budget} onChange={setA('monthly_budget')} className={inputCls} type="number" data-testid="adminv3-corporate-employee-add-budget" />
            </div>
            <div className="mt-2 flex justify-end">
              <Button variant="default" size="sm" onClick={submitAdd} disabled={!addValid || addM.loading} data-testid="adminv3-corporate-employee-add-confirm">
                {addM.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}
                {t('corporate.employees.addConfirm', locale)}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-corporate-employees-dismiss">
            {t('corporate.employees.done', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CorporateEmployeesModal;
